import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import './NewLookTour.css';

/**
 * Guided walkthrough of the New look, played right after a viewer switches
 * from Classic (and on demand via startNewLookTour()). Each step spotlights a
 * `data-tour` hook in the shell; steps whose hook is missing (nav gated by
 * flags or report grants) are skipped, so every viewer sees only what they have.
 */
const PENDING_KEY = 'lodgeic:newLookTour';
const START_EVENT = 'lodgeic:start-new-look-tour';
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

/** Queue the tour for the next New-look mount (survives the shell switch). */
export function queueNewLookTour() {
  try { sessionStorage.setItem(PENDING_KEY, '1'); } catch (e) { /* private mode */ }
}

/** Replay from anywhere under the New look (e.g. Settings → Appearance). */
export function startNewLookTour() {
  window.dispatchEvent(new Event(START_EVENT));
}

function consumePending() {
  try {
    const pending = sessionStorage.getItem(PENDING_KEY) === '1';
    if (pending) sessionStorage.removeItem(PENDING_KEY);
    return pending;
  } catch (e) {
    return false;
  }
}

const STEPS = [
  {
    id: 'welcome',
    route: '/dashboard/overview',
    title: 'Welcome to the new look',
    body: 'A one-minute tour of where everything lives now. You can skip it at any time.',
    next: 'Show me around',
  },
  {
    id: 'overview',
    route: '/dashboard/overview',
    target: '[data-tour="dashboard-tabs"]',
    placement: 'bottom',
    title: 'Your dashboard opens on Overview',
    body: 'Portfolio KPIs at a glance. Revenue, Operations, Financial and Forecast are the next tabs along.',
  },
  {
    id: 'dashboard-nav',
    target: '[data-tour="nav-dashboard"]',
    title: 'Dashboards, in the sidebar',
    body: 'The same dashboard tabs are listed here, so any of them is one click away from every page.',
  },
  {
    id: 'reports',
    target: '[data-tour="group-analytics"]',
    title: 'Reports now live in Analytics',
    body: 'Revenue, Operations and Financial reports sit right under Analytics. Open a group to see its reports.',
  },
  {
    id: 'catalog',
    target: '[data-tour="nav-reports-catalog"]',
    title: 'Every report in one place',
    body: 'All Reports lists everything you have access to, including the ones you subscribe to.',
  },
  {
    id: 'search',
    target: '[data-tour="search"]',
    title: 'Jump anywhere',
    body: `Press ${isMac ? '⌘K' : 'Ctrl K'} to search reports, dashboards and pages.`,
  },
  {
    id: 'account',
    target: '[data-tour="account"]',
    title: 'Make it yours',
    body: 'Settings live in your account menu. Appearance sets your sidebar, panel and chart colours, and can take you back to Classic.',
    next: 'Choose my colours',
    finishRoute: '/settings/appearance',
  },
];

const PAD = 6;
const GAP = 14;
const CARD_W = 360;

function useTargetRect(selector, active) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!active || !selector) { setRect(null); return undefined; }
    let frame = 0;
    const measure = () => {
      const el = document.querySelector(selector);
      const r = el?.getBoundingClientRect();
      setRect(r && r.width > 0 && r.height > 0 ? { top: r.top, left: r.left, width: r.width, height: r.height } : null);
    };
    const el = document.querySelector(selector);
    el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    measure();
    // Sidebar groups expand and pages settle after navigation; keep tracking briefly
    const tick = () => { measure(); frame = requestAnimationFrame(tick); };
    frame = requestAnimationFrame(tick);
    const stop = setTimeout(() => cancelAnimationFrame(frame), 1200);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(stop);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [selector, active]);
  return rect;
}

function cardPosition(rect, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) return { left: (vw - CARD_W) / 2, top: Math.max(24, vh * 0.28), centered: true };
  if (placement === 'bottom' || rect.left + rect.width + GAP + CARD_W > vw - 16) {
    const left = Math.min(Math.max(16, rect.left), vw - CARD_W - 16);
    return { left, top: rect.top + rect.height + PAD + GAP };
  }
  const top = Math.min(Math.max(16, rect.top - 8), vh - 220);
  return { left: rect.left + rect.width + PAD + GAP, top };
}

export default function NewLookTour() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [running, setRunning] = useState(false);
  const [index, setIndex] = useState(0);
  const [missing, setMissing] = useState(() => new Set());

  // Start after the shell's entrance (sidebar slide-out) has settled
  useEffect(() => {
    let timer;
    if (consumePending()) timer = setTimeout(() => { setIndex(0); setRunning(true); }, 1400);
    const onStart = () => { setMissing(new Set()); setIndex(0); setRunning(true); };
    window.addEventListener(START_EVENT, onStart);
    return () => { clearTimeout(timer); window.removeEventListener(START_EVENT, onStart); };
  }, []);

  const steps = useMemo(() => STEPS.filter((s) => !missing.has(s.id)), [missing]);
  const step = running ? steps[Math.min(index, steps.length - 1)] : null;

  useEffect(() => {
    if (step?.route && pathname !== step.route) navigate(step.route);
  }, [step, pathname, navigate]);

  // A hook that never shows up (gated nav) drops its step
  useEffect(() => {
    if (!step?.target) return undefined;
    const timer = setTimeout(() => {
      if (!document.querySelector(step.target)) setMissing((prev) => new Set(prev).add(step.id));
    }, 900);
    return () => clearTimeout(timer);
  }, [step]);

  const rect = useTargetRect(step?.target, running);
  const close = useCallback(() => setRunning(false), []);
  const last = index >= steps.length - 1;
  const next = useCallback(() => {
    if (!last) { setIndex((i) => i + 1); return; }
    setRunning(false);
    if (step?.finishRoute) navigate(step.finishRoute);
  }, [last, step, navigate]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!running) return undefined;
    const onKey = (e) => {
      // Enter on a focused control (the autofocused primary button) already
      // clicks it; advancing here too would skip a step.
      const onControl = e.target?.closest?.('button, a[href], input, select, textarea, [contenteditable="true"]');
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight' || (e.key === 'Enter' && !onControl)) next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, close, next, back]);

  if (!step) return null;
  const waiting = step.target && !rect;
  const pos = cardPosition(step.target ? rect : null, step.placement);

  return createPortal(
    <div className="nlt-root" role="dialog" aria-modal="true" aria-labelledby="nlt-title">
      {rect ? (
        <div
          className="nlt-spotlight"
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
        />
      ) : (
        <div className="nlt-scrim" />
      )}
      {!waiting && (
        <div
          key={step.id}
          className={`nlt-card${pos.centered ? ' nlt-card-centered' : ''}`}
          style={{ top: pos.top, left: pos.left, width: CARD_W }}
        >
          <button type="button" className="nlt-close" onClick={close} aria-label="Skip tour">
            <X size={14} aria-hidden="true" />
          </button>
          {index === 0 && <div className="nlt-eyebrow">New look</div>}
          <h2 id="nlt-title" className="nlt-title">{step.title}</h2>
          <p className="nlt-body">{step.body}</p>
          <div className="nlt-foot">
            <div className="nlt-dots" aria-label={`Step ${index + 1} of ${steps.length}`}>
              {steps.map((s, i) => <span key={s.id} className={i === index ? 'nlt-dot nlt-dot-on' : 'nlt-dot'} />)}
            </div>
            <div className="nlt-actions">
              {index === 0 ? (
                <button type="button" className="nlt-ghost" onClick={close}>Skip</button>
              ) : (
                <button type="button" className="nlt-ghost" onClick={back} aria-label="Back">
                  <ArrowLeft size={14} aria-hidden="true" />
                </button>
              )}
              <button type="button" className="nlt-primary" onClick={next} autoFocus>
                {step.next || (last ? 'Done' : 'Next')}
                {!last && <ArrowRight size={14} aria-hidden="true" />}
              </button>
            </div>
          </div>
          {last && step.finishRoute && (
            <button type="button" className="nlt-link" onClick={close}>I'm all set</button>
          )}
        </div>
      )}
    </div>,
    document.body
  );
}
